import { useState, useMemo } from 'react';
import { Box, Flex, Paragraph, Button, Image } from 'theme-ui';
import { useProductMetafield, useSettings } from '@backpackjs/storefront';
import { Link } from '@snippets';

import { StoreLocator } from './StoreLocator';
import { themed } from './Metadata.theme';

export const Metadata = themed(({ theme, product, selectedVariant }) => {
  const { metafield } = useProductMetafield({
    handle: product?.handle,
    namespace: 'my_fields',
    key: 'specs_and_fitment',
  });
  const [detailsOpen, setDetailsOpen] = useState(true);
  const [specsOpen, setSpecsOpen] = useState(false);
  const [dealerOpen, setDealerOpen] = useState(false);
  const [locallyInjected, setLocallyInjected] = useState(false);

  const settings = useSettings();
  const guides = settings?.product?.specs?.guides;

  const usageGuide = useMemo(() => {
    if (!product?.tags?.length || !guides?.length) return;

    const productTag = product?.tags?.find((tag) =>
      tag.startsWith('usage-guide::')
    );

    const productGuide = guides?.find(
      (guide) =>
        guide?.tag?.toLowerCase() ===
        productTag?.split('usage-guide::')?.[1]?.toLowerCase().trim()
    );

    return productGuide;
  }, [product?.id, guides]);

  const accordions = [
    {
      title: 'Details',
      isOpen: detailsOpen,
      toggle: () => setDetailsOpen(!detailsOpen),
      content: (
        <Box
          sx={{
            width: '100%',
            py: 8,
            borderBottomStyle: product?.descriptionHtml ? 'solid' : 'none',
            borderBottomWidth: '1px',
            borderBottomColor: 'gray',
            'ul, ol': { pl: 0 },
            'p, span': {
              variant: 'text.base',
              mt: 0,
            },
            'p:last-of-type': {
              mb: 0,
            },
          }}
          dangerouslySetInnerHTML={{ __html: product?.descriptionHtml }}
        />
      ),
    },
    {
      title: 'Specs & Fitment',
      isOpen: specsOpen,
      toggle: () => setSpecsOpen(!specsOpen),
      content: (
        <>
          <Paragraph
            sx={{
              py: 8,
              fontSize: [3, 4],
              borderBottomStyle:
                metafield?.value && !usageGuide ? 'solid' : 'none',
              borderBottomWidth: '1px',
              borderBottomColor: 'gray',
              p: { py: 0 },
            }}
            dangerouslySetInnerHTML={{
              __html: metafield?.value?.replaceAll('\n', '<br>'),
            }}
          />
          {usageGuide && (
            <Flex
              sx={{
                variant: 'flex.row.start.center',
                borderBottomStyle: 'solid',
                borderBottomWidth: '1px',
                borderBottomColor: 'gray',
                py: '16px',
              }}
            >
              <Image
                alt={usageGuide.tag}
                src={'/svgs/pdf-logo.svg' || '/svgs/default-image.svg'}
                sx={{ width: '30px', mr: 3 }}
                loading="lazy"
              />
              <Link
                variant="buttons.link.1"
                href={usageGuide?.cta?.url}
                newTab={usageGuide?.cta?.newTab}
              >
                <Paragraph
                  sx={{
                    lineHeight: 18,
                    fontWeight: 500,
                    fontSize: [3, 4],
                  }}
                >
                  {usageGuide?.cta?.text}
                </Paragraph>
              </Link>
            </Flex>
          )}
        </>
      ),
    },
    {
      title: 'Find a Local Retailer',
      isOpen: dealerOpen,
      toggle: () => setDealerOpen(!dealerOpen),
      content: (
        <StoreLocator
          selectedVariant={selectedVariant}
          locallyInjected={locallyInjected}
          setLocallyInjected={setLocallyInjected}
        />
      ),
    },
  ];

  return (
    <Box data-comp={Metadata.displayName} sx={theme.wrapper}>
      {accordions.map(({ title, isOpen, toggle, content }) => (
        <Box key={title} sx={theme.accordion}>
          <Button
            variant="buttons.plain"
            onClick={toggle}
            sx={theme.accordionHeader(isOpen)}
          >
            <Flex sx={theme.accordionHeaderContent}>
              <Paragraph sx={theme.accordionTitle}>{title}</Paragraph>
              <Image
                src={isOpen ? '/svgs/chevron/up.svg' : '/svgs/chevron/down.svg'}
                alt={isOpen ? 'Collapse' : 'Expand'}
                sx={theme.accordionIcon(isOpen)}
              />
            </Flex>
          </Button>
          <Box sx={theme.accordionContent(isOpen)}>
            {content || null}
          </Box>
        </Box>
      ))}
    </Box>
  );
});

Metadata.displayName = 'Metadata';
